// --- external
import { createMachine, assign, sendParent } from "xstate";

// --- internal
import services from "./services";
import { useFeedback } from "../../feedback";

// --- utils
import {
  useTime,
  parseError,
  useModelParser,
  mapToHeadlessError,
  useValidationParser,
  isDirty
} from "../../../utils";
import { responseCodes } from "../../../utils";
import { useSchema, useUischema } from "./utils";
import { get, isEmpty, isEqual, last, map, remove, xorBy } from "lodash-es";

// --- types
import type { AnyEventObject } from "xstate";
import type { PromotionsContext, PromotionModel } from "./types";
import { useI18n } from "../../system";
import { isRestored, isSwapped, parsePromotionDetails } from "./utils";

// -----------------------------------------------------------------------------
export default createMachine(
  {
    //tsTypes: {} as import("./promotions.machine.typegen").Typegen0,
    id: "basketPromotionsManager",
    predictableActionArguments: true,
    initial: "loading",
    context: {} as PromotionsContext,
    states: {
      loading: {
        entry: ["clearError"],
        invoke: {
          src: "load",
          onDone: {
            target: "complete",
            actions: ["setContext", "setSchemas"]
          },
          onError: {
            target: "error",
            actions: ["setError", "setFeedbackError"]
          }
        }
      },
      // ---

      checking: {
        entry: ["clearError"],
        initial: "parsing",
        states: {
          parsing: {
            invoke: {
              src: "parse",
              onDone: {
                target: "validating",
                actions: ["setContext", "setSchemas"]
              }
            }
          },
          validating: {
            invoke: {
              src: "validate",
              onDone: [
                {
                  target: "#valid",
                  cond: "isDirty"
                },
                {
                  target: "#complete"
                }
              ],
              onError: {
                target: "#invalid",
                actions: ["setError"]
              }
            }
          }
        }
      },

      valid: {
        id: "valid",
        always: { target: "processing", cond: "shouldUpdate" },

        on: {
          ADD: {
            target: "processing"
          },
          UPDATE: {
            target: "processing",
            cond: "hasBasket"
          }
        }
      },

      invalid: {
        id: "invalid"
      },

      processing: {
        entry: ["clearError"],
        initial: "update",
        states: {
          update: {
            invoke: {
              src: "add",
              onDone: {
                target: "#processed",
                actions: ["clearAutoUpdate", "clearModel", "clearSwappedIn"]
              },
              onError: [
                {
                  target: "swap",
                  cond: "isCombinationConflict",
                  actions: ["clearAutoUpdate"]
                },
                {
                  target: "#error",
                  actions: ["setError", "clearAutoUpdate", "setFeedbackError"]
                }
              ]
            }
          },
          swap: {
            id: "swap",
            on: { REMOVE: {}, SET: {}, CLEAR: {} },
            invoke: {
              src: "swap",
              onDone: {
                target: "#processed",
                actions: ["setSwappedIn", "clearModel"]
              },
              onError: {
                target: "#error",
                actions: ["setError", "setFeedbackError"]
              }
            }
          },
          remove: {
            invoke: {
              src: "remove",
              onDone: {
                target: "#processed"
              },
              onError: {
                target: "#error",
                actions: ["setError", "setFeedbackError"]
              }
            }
          }
        }
      },

      processed: {
        id: "processed",
        entry: ["prefreshBasket", "refreshBasket"],
        after: { wait: { target: "complete" } }
      },

      complete: {
        id: "complete",
        after: [
          {
            cond: "isRestored",
            delay: "notice",
            target: "complete",
            actions: ["clearSwappedIn", "clearHistory"]
          }
        ],
        on: {
          ADD: { target: "processing" },
          SWAP: { target: "#swap", cond: "isSwapped" }
        }
        // type: "final"
      },

      error: {
        id: "error",
        on: {
          ADD: { target: "processing" },
          SWAP: { target: "#swap", cond: "isSwapped" }
        }
      }
    },
    on: {
      REMOVE: {
        target: "processing.remove",
        actions: ["removePromo", "clearHistory", "clearSwappedIn"]
      },
      CLEAR: {
        target: "checking",
        actions: ["clearModel"]
      },
      SET: {
        target: "checking",
        actions: ["setModel", "setAutoUpdate"]
      },

      UNAUTHENTICATED: {
        target: "loading",
        actions: [
          "clearError",
          "clearModel",
          "clearSchemas",
          "clearHistory",
          "clearSwappedIn"
        ]
      },
      REFRESH: {
        actions: ["refreshContext", "setSchemas"],
        cond: "hasChanged"
      }
    }
  },
  {
    actions: {
      refreshContext: assign(
        (
          { baseModel, history, promotions: current }: PromotionsContext,
          { data: basket }: AnyEventObject
        ) => {
          const promotions = parsePromotionDetails(basket?.promotions ?? []);
          const codes = map(promotions, ({ code }) => ({ promocode: code }));
          // The spawn seeds `promotions` only, so the first refresh reads the earlier codes from it.
          const previous =
            baseModel ?? map(current, ({ code }) => ({ promocode: code }));
          const hasChanged = !isEqual(codes, previous);

          return {
            basketId: basket?.id,
            promotions,
            baseModel: codes,
            history:
              hasChanged && !isEmpty(previous)
                ? [...(history ?? []), previous]
                : history
          };
        }
      ),

      // NB: send PREFRESH with data first for immediate UI update, then REFRESH for full refresh
      prefreshBasket: sendParent(
        (_context: PromotionsContext, { data }: AnyEventObject) => {
          return {
            type: "PREFRESH",
            data
          };
        }
      ),

      refreshBasket: sendParent({ type: "REFRESH" }),

      setContext: assign(
        (_context: PromotionsContext, { data }: AnyEventObject) => data
      ),

      setSchemas: assign({
        schema: (context: PromotionsContext) => useSchema(context),
        uischema: (context: PromotionsContext) => useUischema(context),
        model: ({ schema, model }) => {
          if (!schema) return model;
          return useModelParser<PromotionModel>(schema, model);
        }
      }),

      clearSchemas: assign({
        schema: undefined,
        uischema: undefined
      }),

      setModel: assign({
        model: (
          { schema, model }: PromotionsContext,
          { data }: AnyEventObject
        ) => {
          if (!schema) return data ?? model;
          return useModelParser<PromotionModel>(schema, data ?? model);
        }
      }),

      clearModel: assign({
        model: undefined
      }),

      removePromo: assign({
        promotions: (
          { promotions }: PromotionsContext,
          { data }: AnyEventObject
        ) => {
          const id = get(data, "id", data);
          if (promotions?.length && id) {
            remove(promotions, ["id", id]);
          }
          return promotions;
        }
      }),

      clearHistory: assign({ history: undefined }),

      // A conflict swap still holds the entered code in `model`; a SWAP back does not.
      setSwappedIn: assign({
        swappedIn: ({ model, swappedIn }: PromotionsContext) =>
          model?.promocode ? model : swappedIn
      }),

      clearSwappedIn: assign({ swappedIn: undefined }),

      setAutoUpdate: assign({
        autoupdate: (_context: PromotionsContext, { update }: AnyEventObject) =>
          !!update
      }),
      clearAutoUpdate: assign({
        autoupdate: false
      }),

      // ---

      setFeedbackError: (
        { error }: PromotionsContext,
        _event: AnyEventObject
      ) => {
        const { t } = useI18n();
        if (!error || error.status < 500) return;

        useFeedback().addError({
          title: t("error.promotion_update_failed"),
          copy: error?.message,
          data: error?.data
        });
      },

      setError: assign({
        error: (_context: PromotionsContext, { data }: AnyEventObject) => {
          let error = mapToHeadlessError(data);

          if (
            error?.status == responseCodes.Unprocessable_Entity ||
            error?.status == responseCodes.Conflict
          ) {
            if (isEmpty(error?.data)) {
              // ensure we have a valid error object
              error.data = parseError(error?.message, "promocode");
            } else {
              error.data = useValidationParser(error);
            }
          }
          return error;
        }
      }),

      clearError: assign({ error: undefined })
    },

    guards: {
      isDirty: ({ baseModel, model }, _event) =>
        isDirty(model, baseModel) && !isEmpty(model?.promocode),
      hasBasket: ({ basketId }, _event) => !!basketId,
      isSwapped: (context, _event) => isSwapped(context),
      isRestored: (context, _event) => isRestored(context),
      isCombinationConflict: ({ promotions }, { data }: AnyEventObject) =>
        mapToHeadlessError(data)?.status === responseCodes.Conflict &&
        !isEmpty(promotions),
      hasChanged: ({ promotions, basketId }, { data }: AnyEventObject) =>
        !!xorBy(promotions, data?.promotions, "id")?.length ||
        basketId !== data?.id,
      shouldUpdate: ({ autoupdate, basketId }, _event) =>
        !!autoupdate && !!basketId
    },

    delays: {
      error: () => useTime().ERROR,
      wait: () => useTime().WAIT,
      notice: () => useTime().NOTICE
    },

    services
  }
);
